import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTaskStore } from '../store/taskStore'
import ImageCarousel from '../components/ImageCarousel'

export default function TaskItemDetailPage() {
  const { t } = useTranslation(['worker', 'common'])
  const { taskId, itemId } = useParams<{ taskId: string; itemId: string }>()
  const navigate = useNavigate()
  const { currentTask, myAssignments, submissions } = useTaskStore()

  const item = currentTask?.items.find((i) => i.id === itemId)
  const assignment = myAssignments.find(
    (a) => a.task_id === taskId && ['claimed', 'in_progress', 'submitted'].includes(a.status)
  )
  const submission = submissions[itemId ?? '']

  if (!item) {
    return (
      <div className="p-4">
        <button
          className="text-sm opacity-60"
          style={{ color: 'var(--tg-theme-link-color)' }}
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
        <p className="text-sm opacity-50 mt-4 text-center" style={{ color: 'var(--tg-theme-text-color)' }}>
          {t('common:error.failedToLoad')}
        </p>
      </div>
    )
  }

  const isFound = submission?.submission_type === 'found'

  // Build ordered image list: gallery images first (or item_image_url fallback)
  const allImages = item.item_images?.length
    ? item.item_images.map((img) => img.image_url)
    : item.item_image_url
    ? [item.item_image_url]
    : []

  return (
    <div className="min-h-screen pb-28 safe-area-bottom" style={{ background: 'var(--app-page-bg)' }}>
      {/* Images */}
      {allImages.length > 0 ? (
        <div className="w-full">
          {/* Overlay buttons — height:0 so they float over the carousel without affecting layout */}
          <div className="relative w-full" style={{ height: 0, zIndex: 10 }}>
            <button
              className="absolute top-4 left-4 rounded-full px-3 py-1.5 text-sm font-medium"
              style={{ background: 'rgba(0,0,0,0.45)', color: 'white', backdropFilter: 'blur(4px)' }}
              onClick={() => navigate(-1)}
            >
              ← Back
            </button>
            {submission && (
              <div
                className="absolute top-4 right-4 rounded-full px-3 py-1.5 text-sm font-semibold"
                style={{
                  background: isFound ? 'rgba(34,197,94,0.85)' : 'rgba(239,68,68,0.85)',
                  color: 'white',
                }}
              >
                {isFound ? t('submission.found') : t('submission.notFound')}
              </div>
            )}
          </div>
          <ImageCarousel images={allImages} height={Math.min(window.innerWidth, 480)} showThumbs />
        </div>
      ) : (
        <div className="p-4">
          <button
            className="text-sm opacity-60"
            style={{ color: 'var(--tg-theme-link-color)' }}
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
        </div>
      )}

      <div className="p-4 space-y-3">
        <div>
          <h1 className="text-xl font-bold" style={{ color: 'var(--tg-theme-text-color)' }}>
            {item.product_name}
          </h1>
          {item.product_description && (
            <p className="text-sm opacity-60 mt-1 leading-relaxed" style={{ color: 'var(--tg-theme-text-color)' }}>
              {item.product_description}
            </p>
          )}
        </div>

        {/* Submitted price */}
        {submission && isFound && (submission.price_uzs != null || submission.price_usd != null) && (
          <div
            className="rounded-xl px-4 py-3 text-sm font-semibold"
            style={{ background: '#22c55e18', color: '#22c55e' }}
          >
            {t('taskItemDetail.reportedPrice')}:{' '}
            {submission.price_usd != null
              ? `$${Number(submission.price_usd).toLocaleString()}`
              : `${Number(submission.price_uzs).toLocaleString()} UZS`}
          </div>
        )}
      </div>

      {/* Bottom action */}
      <div className="fixed bottom-0 left-0 right-0 p-4 safe-area-bottom" style={{ background: 'var(--app-page-bg)' }}>
        {assignment ? (
          <button
            className="w-full py-3 rounded-2xl font-semibold text-base"
            style={{ background: 'var(--tg-theme-button-color)', color: 'var(--tg-theme-button-text-color)' }}
            onClick={() => navigate(`/assignments/${assignment.id}/submit/${item.id}`)}
          >
            {submission ? t('taskItemDetail.updateSubmission') : t('taskItemDetail.submitItem')}
          </button>
        ) : (
          <div
            className="w-full py-3 rounded-2xl font-semibold text-base text-center opacity-50"
            style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-text-color)' }}
          >
            {t('taskItemDetail.claimToSubmit')}
          </div>
        )}
      </div>
    </div>
  )
}
